export const createViewerMenusController = ({
  state,
  labelsPanel,
  labelsMarkersList,
  labelsObjectTypesList,
  labelsThingTypesList,
  labelsIntegrationsList,
  labelsIntegrationObjectTypesList,
  referenceLayerMenuList,
  referenceLayerList,
  activeReferenceLayer,
  cloneCablingPlanSettings,
  escapeHtml,
  placementObjectTypeLabel,
  builtInPlacementObjectTypes,
  uniqueSortedKinds,
  placementObjectType,
  placementLabelMappings
}) => {
  const referenceLayerStatusMessage = (referenceLayer = activeReferenceLayer()) => {
    if (!referenceLayer) return "No reference layer selected."
    const displayName = referenceLayer.id === state.activeReferenceLayerId ? state.cablingPlanName || referenceLayer.name : referenceLayer.name
    if (referenceLayer.processed) return `${displayName} is ready for viewer use.`
    if (referenceLayer.processingStatus === "processing") return `${displayName} is being generated from the uploaded PDF.`
    if (referenceLayer.processingStatus === "failed") {
      return referenceLayer.processingError || `${displayName} failed to process.`
    }

    return `${displayName} does not have a generated image yet.`
  }

  const allLabelObjectTypes = () =>
    builtInPlacementObjectTypes
      .slice()
      .sort((left, right) => placementObjectTypeLabel(left).localeCompare(placementObjectTypeLabel(right)))

  const allLabelThingTypes = () =>
    uniqueSortedKinds(
      Array.from(state.placementsById.values()).map((placement) => placement.thing?.kind)
    )

  const allLabelIntegrations = () =>
    Array.from(
      new Map(
        Array.from(state.nodeMappingsByPlacementId.values())
          .flat()
          .filter((mapping) => mapping.integration_id && mapping.integration_name)
          .map((mapping) => [mapping.integration_id, { value: mapping.integration_id, label: mapping.integration_name }])
      ).values()
    ).sort((left, right) => left.label.localeCompare(right.label))

  const integrationObjectTypeKey = (mapping) => `${mapping.integration_id}:${mapping.object_type}`

  const allLabelIntegrationObjectTypes = () =>
    Array.from(
      new Map(
        Array.from(state.nodeMappingsByPlacementId.values())
          .flat()
          .filter((mapping) => mapping.integration_id && mapping.object_type)
          .map((mapping) => [
            integrationObjectTypeKey(mapping),
            {
              value: integrationObjectTypeKey(mapping),
              label: `${mapping.integration_name || "Integration"}: ${mapping.object_label || mapping.object_type}`
            }
          ])
      ).values()
    ).sort((left, right) => left.label.localeCompare(right.label))

  const hasVisibleValues = (allValues, hiddenValues) => allValues.some((value) => !hiddenValues.has(value))

  const labelVisibleForPlacement = (placement) => {
    if (!state.labelsVisible) return false

    const objectType = placementObjectType(placement)
    const thingType = placement.thing?.kind?.toString().trim() || ""
    const mappings = placementLabelMappings(placement)
    const availableObjectTypes = allLabelObjectTypes()
    const availableThingTypes = allLabelThingTypes()
    const availableIntegrationIds = allLabelIntegrations().map((option) => option.value)
    const availableIntegrationObjectTypes = allLabelIntegrationObjectTypes().map((option) => option.value)
    const objectTypeFilterActive = !hasVisibleValues(availableObjectTypes, state.hiddenLabelObjectTypes)
      ? availableObjectTypes.length > 0
      : state.hiddenLabelObjectTypes.size > 0
    const thingTypeFilterActive = !hasVisibleValues(availableThingTypes, state.hiddenLabelThingTypes)
      ? availableThingTypes.length > 0
      : state.hiddenLabelThingTypes.size > 0
    const integrationFilterActive = !hasVisibleValues(availableIntegrationIds, state.hiddenLabelIntegrationIds)
      ? availableIntegrationIds.length > 0
      : state.hiddenLabelIntegrationIds.size > 0
    const integrationObjectTypeFilterActive = !hasVisibleValues(availableIntegrationObjectTypes, state.hiddenLabelIntegrationObjectTypes)
      ? availableIntegrationObjectTypes.length > 0
      : state.hiddenLabelIntegrationObjectTypes.size > 0
    const objectTypeVisible = objectType && !state.hiddenLabelObjectTypes.has(objectType)
    const thingTypeVisible = thingType && !state.hiddenLabelThingTypes.has(thingType)
    const integrationVisible = mappings.some((mapping) => !state.hiddenLabelIntegrationIds.has(mapping.integration_id))
    const integrationObjectTypeVisible = mappings.some((mapping) => !state.hiddenLabelIntegrationObjectTypes.has(integrationObjectTypeKey(mapping)))
    const activeMatches = []

    if (objectTypeFilterActive) activeMatches.push(Boolean(objectTypeVisible))
    if (thingTypeFilterActive) activeMatches.push(Boolean(thingTypeVisible))
    if (integrationFilterActive) activeMatches.push(Boolean(integrationVisible))
    if (integrationObjectTypeFilterActive) activeMatches.push(Boolean(integrationObjectTypeVisible))

    if (activeMatches.length > 0) return activeMatches.some(Boolean)

    return true
  }

  const renderLabelsMenu = () => {
    if (!labelsPanel || !labelsMarkersList || !labelsObjectTypesList || !labelsThingTypesList || !labelsIntegrationsList || !labelsIntegrationObjectTypesList) return

    labelsMarkersList.innerHTML = `
      <label class="plan-viewer-labels-option">
        <input type="checkbox" data-viewer-label-markers${state.markersVisible ? " checked" : ""}>
        <span>Navigation points</span>
      </label>
    `

    labelsObjectTypesList.innerHTML = allLabelObjectTypes()
      .map((objectType) => `
        <label class="plan-viewer-labels-option">
          <input type="checkbox" data-viewer-label-object-type="${escapeHtml(objectType)}"${state.hiddenLabelObjectTypes.has(objectType) ? "" : " checked"}>
          <span>${escapeHtml(placementObjectTypeLabel(objectType))}</span>
        </label>
      `)
      .join("") || '<p class="plan-viewer-labels-empty">No object types yet.</p>'

    labelsThingTypesList.innerHTML = allLabelThingTypes()
      .map((thingType) => `
        <label class="plan-viewer-labels-option">
          <input type="checkbox" data-viewer-label-thing-type="${escapeHtml(thingType)}"${state.hiddenLabelThingTypes.has(thingType) ? "" : " checked"}>
          <span>${escapeHtml(thingType)}</span>
        </label>
      `)
      .join("") || '<p class="plan-viewer-labels-empty">No types yet.</p>'

    labelsIntegrationsList.innerHTML = allLabelIntegrations()
      .map((integration) => `
        <label class="plan-viewer-labels-option">
          <input type="checkbox" data-viewer-label-integration-id="${escapeHtml(integration.value)}"${state.hiddenLabelIntegrationIds.has(integration.value) ? "" : " checked"}>
          <span>${escapeHtml(integration.label)}</span>
        </label>
      `)
      .join("") || '<p class="plan-viewer-labels-empty">No integrations yet.</p>'

    labelsIntegrationObjectTypesList.innerHTML = allLabelIntegrationObjectTypes()
      .map((definition) => `
        <label class="plan-viewer-labels-option">
          <input type="checkbox" data-viewer-label-integration-object-type="${escapeHtml(definition.value)}"${state.hiddenLabelIntegrationObjectTypes.has(definition.value) ? "" : " checked"}>
          <span>${escapeHtml(definition.label)}</span>
        </label>
      `)
      .join("") || '<p class="plan-viewer-labels-empty">No integration object types yet.</p>'
  }

  const renderReferenceLayerMenu = () => {
    if (!referenceLayerMenuList) return

    referenceLayerMenuList.innerHTML = state.referenceLayers
      .map((referenceLayer) => {
        const visible = state.visibleReferenceLayerIds.has(referenceLayer.id)
        const statusLabel = referenceLayer.processed
          ? "Ready"
          : referenceLayer.processingStatus === "processing"
            ? "Processing"
            : referenceLayer.processingStatus === "failed"
              ? "Failed"
              : "Pending"

        return `
          <button
            type="button"
            class="plan-viewer-reference-layer-option${visible ? " is-active" : ""}"
            data-reference-layer-menu-id="${escapeHtml(referenceLayer.id)}"
            aria-pressed="${visible ? "true" : "false"}">
            <span class="plan-viewer-reference-layer-option-main">
              <span class="plan-viewer-reference-layer-option-name">${escapeHtml(referenceLayer.name)}</span>
              <span class="plan-viewer-reference-layer-option-status">${escapeHtml(statusLabel)}</span>
            </span>
            ${visible ? '<i class="fa-solid fa-check" aria-hidden="true"></i>' : ""}
          </button>
        `
      })
      .join("") || '<p class="plan-viewer-labels-empty">No reference layers yet.</p>'
  }

  const renderReferenceLayerList = () => {
    if (!referenceLayerList) return

    referenceLayerList.innerHTML = state.referenceLayers
      .map((referenceLayer) => {
        const active = referenceLayer.id === state.activeReferenceLayerId
        const expanded = referenceLayer.id === state.referenceLayerEditorOpenId
        const displayName = referenceLayer.name
        const statusLabel = referenceLayer.processed
          ? "Ready"
          : referenceLayer.processingStatus === "processing"
            ? "Processing"
            : referenceLayer.processingStatus === "failed"
              ? "Failed"
              : "Pending"
        const layerSettings = cloneCablingPlanSettings(referenceLayer.settings || {})
        const layerStatusMessage = referenceLayerStatusMessage(referenceLayer)
        const defaultVisibleValue = referenceLayer.defaultVisible ? "true" : "false"
        const visibleOnCanvas = state.visibleReferenceLayerIds.has(referenceLayer.id)

        return `
          <div class="plan-reference-layer-editor${active ? " is-active" : ""}">
            <button
              type="button"
              class="plan-accordion-trigger${expanded ? " is-expanded" : ""}"
              data-reference-layer-editor-toggle-id="${escapeHtml(referenceLayer.id)}"
              aria-expanded="${expanded ? "true" : "false"}">
              <span class="plan-reference-layer-editor-heading">
                <span class="plan-reference-layer-editor-title-row">
                  <span>${escapeHtml(displayName)}</span>
                  <span class="plan-reference-layer-editor-badges">
                    <span class="plan-mapping-chip">${escapeHtml(statusLabel)}</span>
                    ${visibleOnCanvas ? '<span class="plan-mapping-chip">Visible</span>' : ""}
                  </span>
                </span>
                <span class="plan-reference-layer-editor-subtitle">${escapeHtml(layerStatusMessage)}</span>
              </span>
              <i class="fa-solid fa-chevron-down" aria-hidden="true"></i>
            </button>
            <div class="plan-accordion-content" data-reference-layer-editor-panel-id="${escapeHtml(referenceLayer.id)}"${expanded ? "" : " hidden"}>
              <div class="plan-cabling-editor-subsection">
                <h3 class="plan-cabling-editor-subheading">Layer</h3>
              </div>
              <div class="plan-cabling-editor-grid">
                <label class="plan-mapping-label">
                  Layer Name
                  <input type="text" class="plan-mapping-input" data-reference-layer-setting="name" data-reference-layer-id="${escapeHtml(referenceLayer.id)}" value="${escapeHtml(displayName)}">
                </label>
                <label class="plan-mapping-label">
                  Show On Load
                  <select class="plan-mapping-input" data-reference-layer-setting="defaultVisible" data-reference-layer-id="${escapeHtml(referenceLayer.id)}">
                    <option value="true"${defaultVisibleValue === "true" ? " selected" : ""}>Visible</option>
                    <option value="false"${defaultVisibleValue === "false" ? " selected" : ""}>Hidden</option>
                  </select>
                </label>
                <label class="plan-mapping-label">
                  Replace PDF
                  <input type="file" accept=".pdf,application/pdf" class="plan-mapping-input" data-reference-layer-replace-file-id="${escapeHtml(referenceLayer.id)}">
                </label>
              </div>
              <div class="plan-cabling-editor-actions">
                <button type="button" class="btn btn-ghost" data-reference-layer-replace-submit-id="${escapeHtml(referenceLayer.id)}">Upload Replacement PDF</button>
                <button
                  type="button"
                  class="btn btn-ghost plan-reference-layer-delete-button"
                  data-reference-layer-delete-id="${escapeHtml(referenceLayer.id)}"
                  aria-label="Delete ${escapeHtml(displayName)}"
                  title="Delete ${escapeHtml(displayName)}">
                  <i class="fa-solid fa-trash" aria-hidden="true"></i>
                </button>
              </div>
              <p>${escapeHtml(layerStatusMessage)}</p>

              <div class="plan-cabling-editor-subsection">
                <h3 class="plan-cabling-editor-subheading">Transform</h3>
              </div>
              <div class="plan-cabling-editor-grid">
                <label class="plan-mapping-label">
                  Scale
                  <input type="number" step="0.01" min="0.01" class="plan-mapping-input" data-reference-layer-setting="scale" data-reference-layer-id="${escapeHtml(referenceLayer.id)}" value="${layerSettings.scale}">
                </label>
                <label class="plan-mapping-label">
                  Rotation
                  <input type="number" step="0.1" class="plan-mapping-input" data-reference-layer-setting="rotationDegrees" data-reference-layer-id="${escapeHtml(referenceLayer.id)}" value="${layerSettings.rotationDegrees}">
                </label>
                <label class="plan-mapping-label">
                  Offset X
                  <input type="number" step="0.1" class="plan-mapping-input" data-reference-layer-setting="offsetX" data-reference-layer-id="${escapeHtml(referenceLayer.id)}" value="${layerSettings.offsetX}">
                </label>
                <label class="plan-mapping-label">
                  Offset Z
                  <input type="number" step="0.1" class="plan-mapping-input" data-reference-layer-setting="offsetZ" data-reference-layer-id="${escapeHtml(referenceLayer.id)}" value="${layerSettings.offsetZ}">
                </label>
                <label class="plan-mapping-label">
                  Elevation
                  <input type="number" step="0.01" class="plan-mapping-input" data-reference-layer-setting="elevation" data-reference-layer-id="${escapeHtml(referenceLayer.id)}" value="${layerSettings.elevation}">
                </label>
                <label class="plan-mapping-label">
                  Opacity
                  <input type="number" step="0.01" min="0" max="1" class="plan-mapping-input" data-reference-layer-setting="opacity" data-reference-layer-id="${escapeHtml(referenceLayer.id)}" value="${layerSettings.opacity}">
                </label>
              </div>

              <div class="plan-cabling-editor-subsection">
                <h3 class="plan-cabling-editor-subheading">Image Treatment</h3>
              </div>
              <div class="plan-cabling-editor-grid">
                <label class="plan-mapping-label">
                  White Threshold
                  <input type="number" step="1" min="0" max="255" class="plan-mapping-input" data-reference-layer-setting="whiteThreshold" data-reference-layer-id="${escapeHtml(referenceLayer.id)}" value="${layerSettings.whiteThreshold}">
                </label>
                <label class="plan-mapping-label">
                  White Feather
                  <input type="number" step="1" min="0" max="255" class="plan-mapping-input" data-reference-layer-setting="whiteFeather" data-reference-layer-id="${escapeHtml(referenceLayer.id)}" value="${layerSettings.whiteFeather}">
                </label>
              </div>
              <p>White pixels above the threshold become transparent. Feather softens the edge so anti-aliased lines stay clean.</p>
            </div>
          </div>
        `
      })
      .join("") || '<p class="plan-mapping-empty">No reference layers yet.</p>'
  }

  const applyViewerLabelsVisibilityPreference = () => {
    if (state.viewerSettings.labelsVisible) {
      state.labelsVisible = true
      state.hiddenLabelObjectTypes.clear()
      state.hiddenLabelThingTypes.clear()
      state.hiddenLabelIntegrationIds.clear()
      state.hiddenLabelIntegrationObjectTypes.clear()
      return
    }

    state.labelsVisible = false
    state.hiddenLabelObjectTypes = new Set(allLabelObjectTypes())
    state.hiddenLabelThingTypes = new Set(allLabelThingTypes())
    state.hiddenLabelIntegrationIds = new Set(allLabelIntegrations().map((option) => option.value))
    state.hiddenLabelIntegrationObjectTypes = new Set(allLabelIntegrationObjectTypes().map((option) => option.value))
  }

  return {
    referenceLayerStatusMessage,
    allLabelObjectTypes,
    allLabelThingTypes,
    allLabelIntegrations,
    allLabelIntegrationObjectTypes,
    labelVisibleForPlacement,
    renderLabelsMenu,
    renderReferenceLayerMenu,
    renderReferenceLayerList,
    applyViewerLabelsVisibilityPreference
  }
}
